import { getOrderAmountDisplay, type OrderAmountDisplay } from './store-fulfillment';
import type { SupportedLocale } from '../i18n';
import { formatCourierPackageTag } from './courier-manifest';
import { formatPrice } from './format';
import { buildOrderDeliveryDisplay } from './order-delivery-display';
import { formatPublicOrderReference, isolateOrderReferenceForLocale } from './order-reference';
import type { CourierOrderAssignmentSummary } from '../types/courier';
import type { Order } from '../types/order';
import type { Store } from '../types/store';

export interface AdminShippingLabelInput {
  readonly order: Order;
  readonly courierAssignment: CourierOrderAssignmentSummary | null;
  readonly store: Pick<Store, 'name' | 'phone_number'> | null;
  readonly locale: SupportedLocale;
  readonly printedAt: Date;
}

export interface AdminShippingLabelViewModel {
  readonly orderReference: string;
  readonly packageTag: string | null;
  readonly batchCode: string | null;
  readonly batchOrderCount: number | null;
  readonly packageTotal: string;
  readonly packageTotalLabelKey: OrderAmountDisplay['labelKey'];
  readonly recipientName: string;
  readonly recipientPhone: string | null;
  readonly destination: string;
  readonly addressLines: readonly string[];
  readonly senderName: string | null;
  readonly senderPhone: string | null;
  readonly deliveryNotes: string | null;
  readonly printedAt: string;
  readonly isSingleOrderLabel: true;
  readonly qrPayload: null;
}

function cleanText(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
}

export function buildAdminShippingLabelViewModel(
  input: AdminShippingLabelInput,
): AdminShippingLabelViewModel {
  const { order, courierAssignment, store, locale, printedAt } = input;
  const packageTag = cleanText(courierAssignment?.package_tag);
  const batchOrderCount = courierAssignment?.batch_order_count;
  const deliveryDisplay = buildOrderDeliveryDisplay(order, locale);

  return {
    orderReference: isolateOrderReferenceForLocale(formatPublicOrderReference(order.id), locale),
    packageTag: packageTag ? formatCourierPackageTag(packageTag) : null,
    batchCode: cleanText(courierAssignment?.display_code),
    batchOrderCount:
      typeof batchOrderCount === 'number' && batchOrderCount > 0 ? batchOrderCount : null,
    packageTotal: formatPrice(getOrderAmountDisplay(order).amountCents, locale),
    packageTotalLabelKey: getOrderAmountDisplay(order).labelKey,
    recipientName: deliveryDisplay.recipientName ?? '',
    recipientPhone: deliveryDisplay.phone,
    destination: deliveryDisplay.destination,
    addressLines: deliveryDisplay.detailLines.filter((line) => line !== deliveryDisplay.phone),
    senderName: cleanText(store?.name),
    senderPhone: cleanText(store?.phone_number),
    deliveryNotes: cleanText(order.customer_notes),
    printedAt: new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(printedAt),
    isSingleOrderLabel: true,
    qrPayload: null,
  };
}
